import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { AdminUsers } from 'libs/end-to-end/src/admin';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import React, { Fragment, useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const UsersPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const language = useLanguage();
  const navigate = useNavigateSafe();

  const [users, setUsers] = useState<AdminUsers | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteUserPrompt, setDeleteUserPrompt] = useState<string | null>(null);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (!isAlive() || !myUser || myUser.roleAssignments !== 'superAdmin') {
        return;
      }

      // Here we can fetch our users list from the API
      const getUsersResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/admin/users`,
        requestOptions: {
          method: 'GET',
        },
        responseSchema: AdminUsers,
      });

      if (!isAlive()) {
        return;
      }

      if (!getUsersResult) {
        return;
      }

      setUsers(
        getUsersResult.resultType === 'err' ? null : getUsersResult.value,
      );
    },
    [myUser, userAuthenticatedApiCall],
  );

  const deleteUser = useAsyncCallback(
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || !deleteUserPrompt) {
        return;
      }

      try {
        setIsDeletingUser(true);

        const deleteUserResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/admin/users/${deleteUserPrompt}`,
          requestOptions: {
            method: 'DELETE',
          },
          responseSchema: GenericSuccess,
        });

        if (!isAlive()) {
          return;
        }

        if (!deleteUserResult || deleteUserResult.resultType === 'err') {
          return;
        }

        setUsers((prevUsers) => {
          if (!prevUsers) {
            return prevUsers;
          }

          return {
            ...prevUsers,
            users: prevUsers.users.filter((u) => u.id !== deleteUserPrompt),
          };
        });
      } finally {
        setIsDeletingUser(false);
        setDeleteUserPrompt(null);
      }
    },
    [deleteUserPrompt, userAuthenticatedApiCall],
  );

  const rows = useMemo(() => {
    if (users === null) {
      return null;
    }

    const actualRows: AdminTableRow[] = [];

    for (const user of users.users) {
      actualRows.push({
        key: user.id,
        columns: [
          {
            columnType: 'text',
            key: 'email',
            text: user.email,
          },
          {
            columnType: 'text',
            key: 'organization_name',
            text: user.organizationName ?? 'None',
          },
          {
            columnType: 'text',
            key: 'organization_role',
            text: user.organizationRole ?? 'None',
          },
          {
            columnType: 'text',
            key: 'is_super_admin',
            text: user.isSuperAdmin ? 'Yes' : 'No',
          },
          {
            columnType: 'button',
            key: 'edit',
            buttonText: 'Edit',
            ariaLabel: `Edit ${user.email}`,
            onClick: () => {
              navigate(`/users/${user.id}`);
            },
          },
          user.isSuperAdmin
            ? {
                columnType: 'text',
                key: 'delete',
                text: 'N/A',
              }
            : {
                columnType: 'button',
                key: 'delete',
                buttonText: 'Delete',
                onClick: () => setDeleteUserPrompt(user.id),
                ariaLabel: `Delete ${user.email}`,
              },
        ],
      });
    }
    return actualRows;
  }, [users, navigate]);

  const ui = useMemo(() => {
    if (isDeletingUser) {
      return <p>Deleting user...</p>;
    }

    return (
      <>
        <p>You can manage users from this screen.</p>

        {rows !== null ? (
          <>
            <S2Typography
              variant="headline-4"
              translatedContent={language.usersPageTableTitle()}
            />
            <div className="flex flex-col items-start">
              <Button
                variant={'primary'}
                behavior={{ type: 'href', href: '/users/new' }}
              >
                New User
              </Button>
            </div>

            <AdminTable
              headings={[
                'Email',
                'Organization',
                'Role',
                'Is Super Admin?',
                'Edit',
                'Delete',
              ]}
              rows={rows}
            />
          </>
        ) : (
          <Spacing direction="horizontal" size="sm">
            <Spinner size={24} />
          </Spacing>
        )}

        <Transition appear show={!!deleteUserPrompt} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-overlay"
            onClose={() => setDeleteUserPrompt(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Are you sure?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this user? You will not
                        be able to recover anything.
                      </p>
                    </div>

                    <div className="mt-4">
                      <LegacyButton
                        variant={LegacyButtonVariant.PRIMARY_LIGHT}
                        onClick={() => deleteUser()}
                        disabled={isDeletingUser}
                        className="mt-4"
                        data-testid={`confirm-delete-user-${deleteUserPrompt}`}
                      >
                        Delete
                      </LegacyButton>

                      <LegacyButton
                        variant={LegacyButtonVariant.PRIMARY_LIGHT}
                        onClick={() => setDeleteUserPrompt(null)}
                        disabled={isDeletingUser}
                        className="mt-4 ml-4"
                      >
                        Cancel
                      </LegacyButton>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </>
    );
  }, [isDeletingUser, rows, language, deleteUserPrompt, deleteUser]);

  return (
    <AdminLayout currentPage={'Users'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Users', to: '/users' },
        ]}
      />
      {ui}
    </AdminLayout>
  );
};
